import { unzipSync } from "fflate"

export type SellInDetected = {
  unknown?: false
  filename: string
  accountCode: string
  accountName: string
  reportType: string
  templateVersion: string
  periodYear: number
  periodMonth: number
  rowCount: number
}

export type SellInUnknown = {
  unknown: true
  filename: string
  reason: string
}

export type SellInResult = SellInDetected | SellInUnknown

const ACCOUNT_NAMES: Record<string, string> = {
  IDG: "Indogrosir",
  IDM: "Indomaret",
  MIDI: "PT MIDI UTAMA INDONESIA Tbk",
  SAT: "PT SUMBER ALFARIA TRIJAYA Tbk",
  SIL: "PT SUMBER INDAH LESTARI",
}

const MONTH_MAP: Record<string, number> = {
  jan: 1, january: 1, januari: 1,
  feb: 2, february: 2, februari: 2,
  mar: 3, march: 3, maret: 3,
  apr: 4, april: 4,
  may: 5, mei: 5,
  jun: 6, june: 6, juni: 6,
  jul: 7, july: 7, juli: 7,
  aug: 8, august: 8, agustus: 8, agu: 8, agt: 8, ags: 8,
  sep: 9, september: 9,
  oct: 10, october: 10, oktober: 10, okt: 10,
  nov: 11, november: 11,
  dec: 12, december: 12, desember: 12, des: 12,
}

export function parseMonthYear(str: string): [number, number] | null {
  if (!str) return null
  const s = str.trim().toLowerCase()

  const match1 = s.match(/(jan|feb|mar|apr|may|mei|jun|jul|aug|agu|agt|ags|sep|oct|okt|nov|dec|des)[a-z]*[\s\-]*'*(20\d{2}|\d{2})/i)
  if (match1) {
    const mStr = match1[1]
    const yStr = match1[2]
    const m = MONTH_MAP[mStr]
    let y = parseInt(yStr, 10)
    if (y < 100) y += 2000
    if (m && y > 0) return [y, m]
  }

  const match2 = s.match(/(\d{4})-(\d{2})/)
  if (match2) {
    return [parseInt(match2[1], 10), parseInt(match2[2], 10)]
  }

  return null
}

// Mirror of B2bSellInFileParser.resolve_period on the server: prefer the period
// encoded in the filename (operators name files deliberately each month) over a
// possibly-stale in-file label cell. Fallbacks are tried in order. Keeping this in
// lockstep with the server ensures the preview shows the same period that will be
// stored, so duplicate-detection stays accurate.
export function resolvePeriod(
  filename: string,
  ...fallbacks: (string | null | undefined)[]
): [number, number] | null {
  const fromName = parseMonthYear(filename)
  if (fromName) return fromName
  for (const candidate of fallbacks) {
    const p = candidate ? parseMonthYear(candidate) : null
    if (p) return p
  }
  return null
}

export function detectAccountFromFilename(filename: string): { code: string; name: string } | null {
  const f = filename.toLowerCase()
  if (f.includes("idm") || f.includes("indomaret")) {
    return { code: "IDM", name: "Indomaret" }
  } else if (f.includes("idg") || f.includes("indogrosir")) {
    return { code: "IDG", name: "Indogrosir" }
  } else if (f.includes("midi")) {
    return { code: "MIDI", name: "PT MIDI UTAMA INDONESIA Tbk" }
  } else if (f.includes("sat") || f.includes("alfaria") || f.includes("alfamart")) {
    return { code: "SAT", name: "PT SUMBER ALFARIA TRIJAYA Tbk" }
  } else if (f.includes("sil") || f.includes("sumber indah")) {
    return { code: "SIL", name: "PT SUMBER INDAH LESTARI" }
  }
  return null
}

// Product category from the filename. Anything marked "SAFI" (or "skincare"/
// "beauty") is the Skincare category; everything else is Reguler. Must mirror
// B2bSellInFileParser.category_from_filename on the server so previews and
// duplicate-detection agree with what actually gets imported.
function categoryFromFilename(filename: string): "skincare" | "reguler" {
  const f = filename.toLowerCase()
  return f.includes("safi") || f.includes("skincare") || f.includes("beauty") ? "skincare" : "reguler"
}

// Content-based category for IDM xlsx files. Mirror of
// B2bSellInFileParser.category_from_products on the server: "skincare" when SAFI is at
// least half of the non-blank product names, else "reguler"; empty list => "reguler".
// The IDM SKINCARE exports carry SAFI products but often no filename marker, so previews
// must derive the category from product content to match what the server stores (keeps
// duplicate-detection accurate and REGULER/SKINCARE of the same period from colliding).
// KEEP IN LOCKSTEP with the server helper.
function categoryFromProducts(names: (string | number | null | undefined)[]): "skincare" | "reguler" {
  const list = names.map((n) => (n ?? "").toString()).filter((n) => n.trim() !== "")
  if (list.length === 0) return "reguler"
  const safi = list.filter((n) => n.toLowerCase().includes("safi")).length
  return safi * 2 >= list.length ? "skincare" : "reguler"
}

// Mirror of B2bSellInFileParser.pivot_period_fallback: pass through a "YYYY-MM" string,
// or convert an Excel date serial (column A MONTH cell) to "YYYY-MM"; otherwise null.
function pivotPeriodFallback(value: string | number | null | undefined): string | null {
  const s = (value ?? "").toString().trim()
  if (/^\d{4}-\d{2}/.test(s)) return s
  if (/^\d+(\.\d+)?$/.test(s)) {
    const serial = parseInt(s, 10)
    const d = new Date(Date.UTC(1899, 11, 30) + serial * 86400000)
    return `${d.getUTCFullYear()}-${(d.getUTCMonth() + 1).toString().padStart(2, "0")}`
  }
  return null
}

export async function parseB2bSellInForPreview(file: File): Promise<SellInResult> {
  const filename = file.name
  const isXls = filename.toLowerCase().endsWith(".xls")

  // For legacy binary .xls files, parse filename metadata only
  if (isXls) {
    const period = parseMonthYear(filename)
    const acc = detectAccountFromFilename(filename)
    if (!period || !acc) {
      return {
        unknown: true,
        filename,
        reason: `Gagal mendeteksi informasi dari nama file .xls. Harap pastikan nama file mengandung nama distributor dan periode (misal: "SI SAT Jan 25.xls").`
      }
    }

    const isQty = filename.toLowerCase().includes("qty")
    const isValue = filename.toLowerCase().includes("value") || filename.toLowerCase().includes("val")
    const isSkincare = filename.toLowerCase().includes("skincare") || filename.toLowerCase().includes("beauty")

    let reportType: string = categoryFromFilename(filename)
    if (acc.code === "IDM" || acc.code === "IDG") {
      const base = categoryFromFilename(filename)
      reportType = isQty ? `${base}_qty` : (isValue ? `${base}_value` : `${base}_qty`)
    }

    return {
      filename,
      accountCode: acc.code,
      accountName: acc.name,
      reportType,
      templateVersion: isSkincare ? "idm_skincare_xls" : (isQty ? "qty_xls" : "wide_xls"),
      periodYear: period[0],
      periodMonth: period[1],
      rowCount: 0 // Cannot parse binary .xls client-side easily
    }
  }

  // For .xlsx, unzip and read sheet structure
  try {
    const buf = await file.arrayBuffer()
    const data = new Uint8Array(buf)
    const decode = (b: Uint8Array) => new TextDecoder().decode(b)

    const meta = unzipSync(data, {
      filter: (f) => f.name === "xl/workbook.xml" || f.name === "xl/_rels/workbook.xml.rels",
    })
    if (!meta["xl/workbook.xml"]) {
      return { unknown: true, filename, reason: "workbook.xml tidak ditemukan dalam file." }
    }

    const wbXml = decode(meta["xl/workbook.xml"])
    const sheetEntries = parseSheetEntries(wbXml)
    const sheetNames = sheetEntries.map((e) => e.name)
    const relsXml = meta["xl/_rels/workbook.xml.rels"] ? decode(meta["xl/_rels/workbook.xml.rels"]) : ""

    if (sheetNames.includes("SellingInPM")) {
      return detectIdgXlsx(data, decode, filename, sheetEntries, relsXml)
    }

    if (sheetNames.includes("Sheet1") && sheetHasData(data, decode, sheetEntries, relsXml, "Sheet1")) {
      return detectSatTallXlsx(data, decode, filename, sheetEntries, relsXml)
    }

    if (sheetNames.includes("Worksheet")) {
      return detectWideXlsx(data, decode, filename, sheetEntries, relsXml)
    }

    if (sheetNames.some((s) => {
      const l = s.toLowerCase()
      return l.startsWith("quantity") || l.startsWith("qty") || l.startsWith("rupiah") || l.startsWith("val")
    })) {
      // The newer IDM pivot layout (MONTH/PRODUCT in row 3 of the QTY sheet) must be
      // checked before the old-combined path, mirroring the server's detect ordering.
      if (idmPivotSignature(data, decode, sheetNames, sheetEntries, relsXml)) {
        return detectIdmPivotXlsx(data, decode, filename, sheetNames, sheetEntries, relsXml)
      }
      return detectIdmCombinedXlsx(data, decode, filename, sheetNames, sheetEntries, relsXml)
    }

    // Try to guess from filename if sheets are not matching any expected layout
    const period = parseMonthYear(filename)
    const acc = detectAccountFromFilename(filename)
    if (period && acc) {
      return {
        filename,
        accountCode: acc.code,
        accountName: acc.name,
        reportType: categoryFromFilename(filename),
        templateVersion: "wide_xlsx",
        periodYear: period[0],
        periodMonth: period[1],
        rowCount: 0
      }
    }

    return {
      unknown: true,
      filename,
      reason: `Template tidak dikenal. Sheet ditemukan: ${sheetNames.join(", ") || "(tidak ada)"}`,
    }
  } catch (err) {
    return {
      unknown: true,
      filename,
      reason: err instanceof Error ? err.message : "Gagal membaca file",
    }
  }
}

// ---------------------------------------------------------------------------
// Sheet parser helpers
// ---------------------------------------------------------------------------

type SheetEntry = { name: string; rId: string }

function parseSheetEntries(wbXml: string): SheetEntry[] {
  const entries: SheetEntry[] = []
  const re = /<sheet\b[^>]*name="([^"]*)"[^>]*r:id="([^"]*)"[^>]*>/g
  let m
  while ((m = re.exec(wbXml)) !== null) {
    entries.push({ name: m[1], rId: m[2] })
  }
  return entries
}

// Whether a worksheet actually contains cell data. Mirrors the server's
// sheet_has_data? so blank leftover sheets (e.g. an empty "Sheet1") don't mis-route
// template detection. Returns false if the sheet can't be resolved.
function sheetHasData(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  entries: SheetEntry[],
  relsXml: string,
  sheetName: string
): boolean {
  const wsPath = resolveSheetPath(entries, relsXml, sheetName)
  if (!wsPath) return false
  const extracted = unzipSync(data, { filter: (f) => f.name === wsPath })
  const xml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  return xml.includes("<v>") || xml.includes("<is>")
}

function resolveSheetPath(
  entries: SheetEntry[],
  relsXml: string,
  sheetName: string
): string | null {
  const entry = entries.find((e) => e.name === sheetName)
  if (!entry) return null
  const relParts = relsXml.split(/<Relationship\b/)
  for (let i = 1; i < relParts.length; i++) {
    if (relParts[i].includes(`Id="${entry.rId}"`)) {
      const targetMatch = relParts[i].match(/\bTarget="([^"]+)"/)
      if (targetMatch) {
        const target = targetMatch[1]
        if (target.startsWith("../")) return target.slice(3)
        if (target.startsWith("/")) return target.slice(1)
        return `xl/${target}`
      }
    }
  }
  return null
}

function parseSharedStrings(bytes: Uint8Array | undefined, decode: (b: Uint8Array) => string): string[] {
  if (!bytes) return []
  const xml = decode(bytes)
  const ss: string[] = []
  for (const part of xml.split("<si>").slice(1)) {
    const matches = [...part.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    ss.push(matches.map((m) => m[1]).join(""))
  }
  return ss
}

function getRowXml(rowParts: string[], rowNumber: number): string {
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    if (attr && parseInt(attr[1], 10) === rowNumber) {
      const end = rowParts[i].indexOf("</row>")
      return end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    }
  }
  return ""
}

function findCellXml(rowXml: string, colLetter: string): string {
  for (const part of rowXml.split("<c ").slice(1)) {
    if (new RegExp(`r="${colLetter}\\d+"`).test(part)) {
      return "<c " + part
    }
  }
  return ""
}


function cellValueForColumn(rowXml: string, colLetter: string, ss: string[]): string | number | null {
  const cellXml = findCellXml(rowXml, colLetter)
  if (!cellXml) return null

  if (cellXml.includes('t="inlineStr"')) {
    const matches = [...cellXml.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    return matches.length > 0 ? unescapeXml(matches.map((m) => m[1]).join("")) : null
  }
  if (cellXml.includes('t="s"')) {
    const m = cellXml.match(/<v>(\d+)<\/v>/)
    return m ? (ss[parseInt(m[1], 10)] ?? null) : null
  }
  if (cellXml.includes('t="str"')) {
    const m = cellXml.match(/<v>([^<]*)<\/v>/)
    return m ? unescapeXml(m[1]) : null
  }
  const m = cellXml.match(/<v>([^<]*)<\/v>/)
  if (m) {
    const rawVal = unescapeXml(m[1])
    const num = parseFloat(rawVal)
    return isNaN(num) ? rawVal : num
  }
  return null
}

function rowCellValues(rowXml: string, ss: string[]): (string | number | null)[] {
  const cells = rowXml.split("<c ").slice(1)
  return cells.map((cellXml) => {
    const colLetter = cellXml.match(/r="([A-Z]+)\d+"/)?.[1]
    return colLetter ? cellValueForColumn(rowXml, colLetter, ss) : null
  })
}

function unescapeXml(str: string): string {
  return str
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&")
}

// ---------------------------------------------------------------------------
// Template Detectors
// ---------------------------------------------------------------------------

function detectIdgXlsx(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetEntries: SheetEntry[],
  relsXml: string
): SellInResult {
  const wsPath = resolveSheetPath(sheetEntries, relsXml, "SellingInPM")
  if (!wsPath) return { unknown: true, filename, reason: "Sheet SellingInPM tidak ditemukan." }

  const extracted = unzipSync(data, {
    filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath,
  })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const rowParts = wsXml.split("<row ")

  const row3 = getRowXml(rowParts, 3)
  const row5 = getRowXml(rowParts, 5)
  if (!row3 || !row5) {
    return { unknown: true, filename, reason: "File Indogrosir tidak lengkap (Baris 3 atau 5 kosong)." }
  }

  const row3Val = rowCellValues(row3, ss).compact().map((v) => v.toString()).join(" ")
  const row5Val = rowCellValues(row5, ss).compact().map((v) => v.toString()).join(" ")

  const isQty = row3Val.includes("Quantity")
  const period = resolvePeriod(filename, row5Val)
  if (!period) {
    return { unknown: true, filename, reason: `Periode tidak ditemukan dalam file IDG: "${row5Val}".` }
  }

  const base = categoryFromFilename(filename)
  const reportType = isQty ? `${base}_qty` : `${base}_value`

  // Count active rows starting at row 9 (qty) or 11 (value)
  const startRow = isQty ? 9 : 11
  let rowCount = 0
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    const rowNum = attr ? parseInt(attr[1], 10) : 0
    if (rowNum < startRow) continue

    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue

    const itemVal = cellValueForColumn(rowXml, "B", ss)?.toString().trim()
    if (itemVal && itemVal !== "TOTAL") {
      rowCount++
    }
  }

  return {
    filename,
    accountCode: "IDG",
    accountName: ACCOUNT_NAMES["IDG"],
    reportType,
    templateVersion: "idg_xlsx",
    periodYear: period[0],
    periodMonth: period[1],
    rowCount,
  }
}

function detectSatTallXlsx(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetEntries: SheetEntry[],
  relsXml: string
): SellInResult {
  const wsPath = resolveSheetPath(sheetEntries, relsXml, "Sheet1")
  if (!wsPath) return { unknown: true, filename, reason: "Sheet Sheet1 tidak ditemukan." }

  const extracted = unzipSync(data, {
    filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath,
  })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const rowParts = wsXml.split("<row ")

  const row3 = getRowXml(rowParts, 3)
  if (!row3) {
    return { unknown: true, filename, reason: "File Sheet1 tidak memiliki data." }
  }

  const monthStr = cellValueForColumn(row3, "B", ss)?.toString().trim()
  const yearVal = cellValueForColumn(row3, "C", ss)?.toString().trim()

  const period = resolvePeriod(filename, `${monthStr ?? ""} ${yearVal ?? ""}`)
  if (!period) {
    return { unknown: true, filename, reason: `Periode tidak valid pada baris 3 (MONTH="${monthStr}", YEAR="${yearVal}").` }
  }

  let rowCount = 0
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    const rowNum = attr ? parseInt(attr[1], 10) : 0
    if (rowNum < 3) continue

    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue

    const plu = cellValueForColumn(rowXml, "E", ss)?.toString().trim()
    if (plu && plu !== "TOTAL") {
      rowCount++
    }
  }

  return {
    filename,
    accountCode: "SAT",
    accountName: ACCOUNT_NAMES["SAT"],
    reportType: categoryFromFilename(filename),
    templateVersion: "tall_xlsx",
    periodYear: period[0],
    periodMonth: period[1],
    rowCount,
  }
}

function detectWideXlsx(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetEntries: SheetEntry[],
  relsXml: string
): SellInResult {
  const wsPath = resolveSheetPath(sheetEntries, relsXml, "Worksheet")
  if (!wsPath) return { unknown: true, filename, reason: "Sheet Worksheet tidak ditemukan." }

  const extracted = unzipSync(data, {
    filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath,
  })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const rowParts = wsXml.split("<row ")

  const row1 = getRowXml(rowParts, 1)
  const row4 = getRowXml(rowParts, 4)
  if (!row1 || !row4) {
    return { unknown: true, filename, reason: "Sheet Worksheet tidak lengkap." }
  }

  const row1Val = rowCellValues(row1, ss).compact().map((v) => v.toString()).join(" ").trim()
  const row4Val = rowCellValues(row4, ss).compact().map((v) => v.toString()).join(" ").trim()

  let accountCode = ""
  if (row1Val.startsWith("PT SUMBER ALFARIA TRIJAYA")) {
    accountCode = "SAT"
  } else if (row1Val.startsWith("PT SUMBER INDAH LESTARI")) {
    accountCode = "SIL"
  } else if (row1Val.startsWith("PT MIDI UTAMA INDONESIA")) {
    accountCode = "MIDI"
  } else {
    // Try filename fallback
    const acc = detectAccountFromFilename(filename)
    if (acc) accountCode = acc.code
    else return { unknown: true, filename, reason: `Judul perusahaan tidak dikenal: "${row1Val}".` }
  }

  const period = resolvePeriod(filename, row4Val)
  if (!period) {
    return { unknown: true, filename, reason: `Periode tidak dapat ditemukan dari baris 4: "${row4Val}".` }
  }

  let rowCount = 0
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    const rowNum = attr ? parseInt(attr[1], 10) : 0
    if (rowNum < 8) continue

    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue

    const plu = cellValueForColumn(rowXml, "B", ss)?.toString().trim()
    if (plu && plu !== "TOTAL") {
      rowCount++
    }
  }

  return {
    filename,
    accountCode,
    accountName: ACCOUNT_NAMES[accountCode],
    reportType: categoryFromFilename(filename),
    templateVersion: "wide_xlsx",
    periodYear: period[0],
    periodMonth: period[1],
    rowCount,
  }
}

// Recognizes the IDM pivot layout: the QTY-like sheet has "MONTH" in row 3 column A and
// "PRODUCT" in row 3 column B. Mirror of B2bSellInFileParser.idm_pivot_signature? — keep
// the gate identical so the preview routes pivot files exactly as the server does.
function idmPivotSignature(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  sheetNames: string[],
  sheetEntries: SheetEntry[],
  relsXml: string
): boolean {
  const qtySheet = sheetNames.find((s) => {
    const l = s.toLowerCase()
    return l.startsWith("qty") || l.startsWith("quantity")
  })
  if (!qtySheet) return false
  const wsPath = resolveSheetPath(sheetEntries, relsXml, qtySheet)
  if (!wsPath) return false

  const extracted = unzipSync(data, {
    filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath,
  })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const row3 = getRowXml(wsXml.split("<row "), 3)
  if (!row3) return false

  const a = cellValueForColumn(row3, "A", ss)?.toString().trim().toUpperCase()
  const b = cellValueForColumn(row3, "B", ss)?.toString().trim().toUpperCase()
  return a === "MONTH" && b === "PRODUCT"
}

// IDM pivot preview: products live in column B (column A is the MONTH date serial), DC
// 3-column blocks start at column C. The browser only needs detection + an estimated row
// count (full parsing is server-side), but its account/category/period must match the
// server (idm_pivot_xlsx, content-based category, filename-first period).
function detectIdmPivotXlsx(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetNames: string[],
  sheetEntries: SheetEntry[],
  relsXml: string
): SellInResult {
  const qtySheet = sheetNames.find((s) => {
    const l = s.toLowerCase()
    return l.startsWith("qty") || l.startsWith("quantity")
  })
  const wsPath = qtySheet ? resolveSheetPath(sheetEntries, relsXml, qtySheet) : null
  if (!wsPath) return { unknown: true, filename, reason: "Sheet QTY tidak ditemukan." }

  const extracted = unzipSync(data, {
    filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath,
  })
  const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
  const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
  const rowParts = wsXml.split("<row ")

  // Products from column B, data rows (row 4+). Skip blank / TOTAL / Grand Total.
  const products: string[] = []
  let rowCount = 0
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    const rowNum = attr ? parseInt(attr[1], 10) : 0
    if (rowNum < 4) continue

    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue

    const product = cellValueForColumn(rowXml, "B", ss)?.toString().trim()
    if (!product || product.toUpperCase() === "TOTAL" || product.toLowerCase() === "grand total") continue
    products.push(product)
    rowCount++
  }

  // Period: filename is authoritative; fall back to the column-A month serial.
  const firstData = getRowXml(rowParts, 4)
  const monthCell = firstData ? cellValueForColumn(firstData, "A", ss)?.toString().trim() : ""
  const period = resolvePeriod(filename, pivotPeriodFallback(monthCell))
  if (!period) {
    return { unknown: true, filename, reason: `Periode tidak dapat ditemukan dari file "${filename}".` }
  }

  return {
    filename,
    accountCode: "IDM",
    accountName: ACCOUNT_NAMES["IDM"],
    reportType: categoryFromProducts(products),
    templateVersion: "idm_pivot_xlsx",
    periodYear: period[0],
    periodMonth: period[1],
    rowCount,
  }
}

// Locate the flat sheet's header row (carries PRODUCT + BRANCH) and its PRODUCT column.
// Mirror of the server flat_header_row: older exports put it on row 2, newer on row 1.
function flatHeaderInfo(
  rowParts: string[],
  ss: string[]
): { headerNum: number; productCol: string | null } {
  for (let n = 1; n <= 5; n++) {
    const rowXml = getRowXml(rowParts, n)
    if (!rowXml) continue
    const vals = rowCellValues(rowXml, ss).map((v) => (v ?? "").toString().trim().toUpperCase())
    if (vals.includes("PRODUCT") && vals.includes("BRANCH")) {
      let productCol: string | null = null
      for (const cellXml of rowXml.split("<c ").slice(1)) {
        const colLetter = cellXml.match(/r="([A-Z]+)\d+"/)?.[1]
        if (!colLetter) continue
        const name = cellValueForColumn(rowXml, colLetter, ss)?.toString().trim().toUpperCase()
        if (name === "PRODUCT") productCol = colLetter
      }
      return { headerNum: n, productCol }
    }
  }
  return { headerNum: 0, productCol: null }
}

// First data row of an old-combined pivot sheet: the row after the metric-header row whose
// column B starts with "Sum of". Mirror of the server combined_layout's data-start anchor.
function combinedDataStart(rowParts: string[], ss: string[]): number {
  for (let i = 1; i < rowParts.length; i++) {
    const attr = rowParts[i].match(/\br="(\d+)"/)
    const rowNum = attr ? parseInt(attr[1], 10) : 0
    if (rowNum === 0) continue
    const end = rowParts[i].indexOf("</row>")
    const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
    const colB = cellValueForColumn(rowXml, "B", ss)?.toString().trim() ?? ""
    if (/^Sum of/i.test(colB)) return rowNum + 1
  }
  return 4
}

function detectIdmCombinedXlsx(
  data: Uint8Array,
  decode: (b: Uint8Array) => string,
  filename: string,
  sheetNames: string[],
  sheetEntries: SheetEntry[],
  relsXml: string
): SellInResult {
  // A non-qty/rupiah sheet that actually has data is a flat summary sheet; a blank
  // leftover (e.g. an empty "Sheet1") must be ignored so the QTY+VALUE path is used.
  const flatSheet = sheetNames.find((s) => {
    const l = s.toLowerCase()
    return (
      !l.startsWith("quantity") && !l.startsWith("qty") && !l.startsWith("rupiah") && !l.startsWith("val") &&
      sheetHasData(data, decode, sheetEntries, relsXml, s)
    )
  })
  let labelCell: string | null = null
  let rowCount = 0
  // Product names drive the content-based category, matching the server (the SKINCARE
  // exports carry SAFI products but no filename marker).
  const products: (string | number | null)[] = []

  if (flatSheet) {
    const wsPath = resolveSheetPath(sheetEntries, relsXml, flatSheet)
    if (wsPath) {
      const extracted = unzipSync(data, {
        filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath,
      })
      const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
      const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
      const rowParts = wsXml.split("<row ")

      // Header row is row 1 or row 2 depending on export vintage; read the real PRODUCT
      // column (and MONTH from column A for a period fallback) rather than a fixed column.
      const { headerNum, productCol } = flatHeaderInfo(rowParts, ss)

      for (let i = 1; i < rowParts.length; i++) {
        const attr = rowParts[i].match(/\br="(\d+)"/)
        const rowNum = attr ? parseInt(attr[1], 10) : 0
        if (rowNum <= headerNum) continue

        const end = rowParts[i].indexOf("</row>")
        const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
        if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue

        if (labelCell === null) {
          const month = cellValueForColumn(rowXml, "A", ss)
          if (month) labelCell = String(month)
        }

        const productVal = productCol ? cellValueForColumn(rowXml, productCol, ss)?.toString().trim() : null
        if (productVal && productVal !== "TOTAL") {
          products.push(productVal)
          rowCount++
        }
      }
    }
  } else {
    // Combined Quantity & Rupiah sheets (no flat sheet): products in column A.
    const qtySheet = sheetNames.find((s) => s.toLowerCase().startsWith("quantity") || s.toLowerCase().startsWith("qty"))
    if (qtySheet) {
      const wsPath = resolveSheetPath(sheetEntries, relsXml, qtySheet)
      if (wsPath) {
        const extracted = unzipSync(data, {
          filter: (f) => f.name === "xl/sharedStrings.xml" || f.name === wsPath,
        })
        const ss = parseSharedStrings(extracted["xl/sharedStrings.xml"], decode)
        const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
        const rowParts = wsXml.split("<row ")
        const dataStart = combinedDataStart(rowParts, ss)

        for (let i = 1; i < rowParts.length; i++) {
          const attr = rowParts[i].match(/\br="(\d+)"/)
          const rowNum = attr ? parseInt(attr[1], 10) : 0
          if (rowNum < dataStart) continue

          const end = rowParts[i].indexOf("</row>")
          const rowXml = end >= 0 ? rowParts[i].slice(0, end) : rowParts[i]
          if (!rowXml.includes("<v>") && !rowXml.includes("<is>")) continue

          const productVal = cellValueForColumn(rowXml, "A", ss)?.toString().trim()
          if (productVal && productVal !== "TOTAL" && productVal !== "Row Labels" && productVal.toLowerCase() !== "grand total") {
            products.push(productVal)
            rowCount++
          }
        }
      }
    }
  }

  const period = resolvePeriod(filename, labelCell)

  if (!period) {
    return { unknown: true, filename, reason: "Periode tidak dapat ditemukan dari file Indomaret Combined." }
  }

  return {
    filename,
    accountCode: "IDM",
    accountName: ACCOUNT_NAMES["IDM"],
    reportType: categoryFromProducts(products),
    templateVersion: "idm_combined_xlsx",
    periodYear: period[0],
    periodMonth: period[1],
    rowCount,
  }
}

// Helper extension checks
declare global {
  interface Array<T> {
    compact(): Exclude<T, null | undefined>[]
  }
}

if (!Array.prototype.compact) {
  Array.prototype.compact = function () {
    return this.filter((v) => v !== null && v !== undefined)
  }
}
